/**
 *  @organization: Nanocosmos GmbH
 *
 *  Pure helpers behind the maxFrameDelay alert widget. Kept apart from the component so the numbers the
 *  chart and the tiles are built from can be tested on their own.
 */

// External Libraries
import moment from 'moment';

// the median math itself is generic and lives in the shared statistics module, re-exported here so this
// module stays the single import site for everything the maxFrameDelay widget is built from
import { median, getIntervalWindows, getWindowMedians } from '../../../../../../util/Statistics';
import { ALERT_DETECTION_INTERVAL_IN_MINUTES } from '../constants';

export { median, getIntervalWindows, getWindowMedians };

// The thresholds the alerting service opens the three maxFrameDelay alerts on. Kept in one place so the
// chart annotations, the tile descriptions and the alert texts cannot drift apart - they are configurable
// server side, so treat them as the documented defaults rather than as guaranteed values.
export const MFD_THRESHOLD = {
   STALL_MS: 2000,
   PERSISTENT_DELAY_MS: 1300,
   PERSISTENT_DELAY_ULL_MS: 800,
   STALL_PREFILTER_MS: 300,
};

// The detection evaluates a sliding window of this length once per minute. Same number as the look-back the
// fine time range of an alert is widened by, so it is taken from there rather than spelled out again.
export const DETECTION_WINDOW_IN_MINUTES = ALERT_DETECTION_INTERVAL_IN_MINUTES;
export const DETECTION_WINDOW_IN_SECONDS = DETECTION_WINDOW_IN_MINUTES * 60;

// cadence the alerting samples the rtmp stats at, which is what makes a full window 20 datapoints long
export const RTMP_STATS_INTERVAL_IN_SECONDS = 15;
export const EXPECTED_DATAPOINTS_PER_WINDOW = DETECTION_WINDOW_IN_SECONDS / RTMP_STATS_INTERVAL_IN_SECONDS;

// Cadence that window is evaluated at, and therefore the one the median line is drawn at: a median per raw
// datapoint would show intermediate windows the detection never decided on.
export const MEDIAN_EVALUATION_INTERVAL_IN_SECONDS = 60;

// number of datapoints of a full window that have to exceed STALL_MS before the stall alert opens
export const STALL_COUNT_PER_WINDOW = 3;

// how long the ingest stalled in total once that many datapoints exceeded the threshold
export const STALL_DURATION_IN_SECONDS = STALL_COUNT_PER_WINDOW * RTMP_STATS_INTERVAL_IN_SECONDS;

// the widest range the raw rtmp timeseries endpoint is queried for in one go, shared by the three alerts
export const MFD_MAX_DISPLAY_RANGE_IN_MS = 2 * 60 * 60 * 1000;

// The find endpoint builds a notification's fields from Elasticsearch term buckets, which deliver a boolean
// as an array and key it on 0/1. So the flag can arrive as true, [true], 1 or 'true' - a strict === true
// would chart the video metric for an audio only stream, i.e. an empty series underneath a firing alert.
export function isAudioStreamAlert(isAudioStream) {
   return [].concat(isAudioStream ?? []).some((flag) => flag === true || flag === 1 || flag === 'true' || flag === '1');
}

// thousands separator, matching the chart's y axis ticks
export function formatThousands(value) {
   return typeof value === 'number' && !Number.isNaN(value) ? value.toLocaleString('en-US') : String(value);
}

// The connection the alert was raised on, i.e. the one the detection evaluated. The alerting service scopes
// its sample to the active connection, and deciding which one that is the way the service does would mean
// porting its logic - the connection details of the notification answer it well enough for a chart: a stream
// normally publishes over one connection at a time, and where a notification lists several, the longest lived
// one is the one the stream ran on. A connection that has not ended yet is measured against the end of the
// alert, since the notification is what the widget knows the stream by.
//
// Returns null when the notification carries no usable connection details. The caller then keeps every
// datapoint, which is the safe reading: charting the whole response says more than charting nothing.
export function getAlertConnectionId(connectionsDetails, alertEnd) {
   const connections = (connectionsDetails ?? []).filter((connection) => !!connection.connectionId);

   if (connections.length === 0) return null;
   if (connections.length === 1) return connections[0].connectionId;

   const getDurationInMs = (connection) => {
      const duration = moment.utc(connection.end || alertEnd).valueOf() - moment.utc(connection.start).valueOf();
      // an unparseable timestamp must not decide which connection is charted
      return Number.isFinite(duration) ? duration : -1;
   };

   // strictly longer, so the first of two equally long connections wins and the pick does not depend on order
   return connections.reduce((longest, connection) =>
      getDurationInMs(connection) > getDurationInMs(longest) ? connection : longest,
   ).connectionId;
}

// The datapoints that describe the alert: those of the connection it was raised for, with a reported frame
// delay. A negative value means the offset was not reported for that datapoint - the detection drops those
// from its sample, so they must not enter the median here either. Without a connection id everything is
// kept, see getAlertConnectionId.
export function getAlertFrameDelayPoints(rtmpData, connectionId) {
   return rtmpData
      .filter((datapoint) => !connectionId || datapoint.connectionId === connectionId)
      .filter((datapoint) => typeof datapoint.maxFrameDelay === 'number' && datapoint.maxFrameDelay >= 0)
      .map((datapoint) => ({ x: datapoint.timestamp * 1000, y: datapoint.maxFrameDelay }));
}

// The figures the tiles show. The two the detection opens an alert on are reported per window, because that
// is what it evaluates: both are the peak over the windows of the displayed range, i.e. what the worst window
// held. An average over the whole range would read far below the threshold of the alert it is described by,
// the longer the range the further below - and would move when the reader widens the range. For the same
// reason the delay itself is reported as its peak rather than as a percentile: a peak does not shrink when
// healthy minutes are added to the range. Windows are the ones the median line is drawn from, so the peak
// median is the peak of that line.
export function getFrameDelayStats(points, windows, isAudioStream) {
   const sortedValues = points.map((point) => point.y).sort((a, b) => a - b);

   if (sortedValues.length === 0) return null;

   const ullCount = sortedValues.filter((value) => value >= MFD_THRESHOLD.PERSISTENT_DELAY_ULL_MS).length;
   const windowMedians = getWindowMedians(windows).map((point) => point.y);
   const windowStallCounts = windows.map(
      ({ values }) => values.filter((value) => value >= MFD_THRESHOLD.STALL_MS).length,
   );

   return {
      // null rather than a figure where the range is shorter than a window: there is nothing the detection
      // would have decided on yet, and a peak over half a window is not that figure
      mfdPeakWindowMedian: windowMedians.length > 0 ? Math.round(Math.max(...windowMedians)) : null,
      mfdPeakFrameDelay: Math.round(sortedValues[sortedValues.length - 1]),
      mfdPeakWindowStalls: windowStallCounts.length > 0 ? Math.max(...windowStallCounts) : null,
      mfdUllShare: Math.round((ullCount / sortedValues.length) * 100),
      mfdTrack: isAudioStream ? 'Audio' : 'Video',
   };
}
